import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "grid min-h-full grid-cols-[auto_1fr] bg-white" };
const _hoisted_2 = { class: "flex min-w-0 flex-col gap-6 bg-white px-5 pt-6 pb-4" };
const _hoisted_3 = { class: "text-2xl font-bold text-(--colors-text-heading) sm:text-3xl" };
import { computed, h, onMounted, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { DataTable, Pager, TableShell, useTableQuery, useTableViews } from "@eudonet/opal";
import { moduleRegistry } from "@/router/moduleRegistry";
import ModuleState from "@/shared/components/ModuleState.vue";
import { buildPagerLabels } from "@/shared/composables/pagerLabels";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { listsInTab, VALUE_LIST_TABS, valueListName, } from "../../domain/valueList";
import { useValueListStore } from "../../stores/useValueListStore";
const ROW_LINK_CLASS = "rounded-xs text-(--colors-text-link) hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-(--colors-text-link)";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ValueListManagerView',
    setup(__props) {
        const ROW_OPTIONS = [10, 30, 50];
        const { t, locale } = useI18n();
        const store = useValueListStore();
        const SidebarComponent = shallowRef(moduleRegistry.getComponentForSlot("sidebar"));
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0], activeViewId: "all" });
        const tableLabels = computed(() => buildTableLabels(t));
        const pagerLabels = computed(() => buildPagerLabels(t));
        const TAB_LABEL_KEYS = {
            all: "valueLists.tabs.all",
            referentials: "valueLists.tabs.referentials",
            contactOrganisation: "valueLists.tabs.contactOrganisation",
            finance: "valueLists.tabs.finance",
        };
        const tableViews = useTableViews({ initialViews: [] });
        const viewTabs = computed(() => VALUE_LIST_TABS.map((tab) => ({
            id: tab,
            label: t(TAB_LABEL_KEYS[tab]),
            count: listsInTab(store.lists, tab).length,
            isPreset: true,
            isDefault: tab === "all",
            querySnapshot: {},
        })));
        watch(viewTabs, (next) => (tableViews.views.value = next), { immediate: true });
        const activeTab = computed(() => {
            const id = tableQuery.state.activeViewId;
            return VALUE_LIST_TABS.find((tab) => tab === id) ?? "all";
        });
        const tabRows = computed(() => listsInTab(store.lists, activeTab.value));
        const totalPages = computed(() => Math.max(1, Math.ceil(tabRows.value.length / tableQuery.state.pageSize)));
        const pageRows = computed(() => {
            const start = (tableQuery.state.page - 1) * tableQuery.state.pageSize;
            return tabRows.value.slice(start, start + tableQuery.state.pageSize);
        });
        const columns = computed(() => [
            {
                id: "name",
                header: t("valueLists.columns.name"),
                cell: ({ row }) => h(RouterLink, {
                    to: { name: "ValueListDetail", params: { code: row.original.code } },
                    class: ROW_LINK_CLASS,
                    "data-testid": `value-list-row-link-${row.original.code}`,
                }, () => valueListName(row.original, locale.value)),
            },
            {
                id: "code",
                header: t("valueLists.columns.code"),
                cell: ({ row }) => row.original.code,
            },
        ]);
        onMounted(() => store.ensureLoaded());
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (SidebarComponent.value)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(SidebarComponent.value), { key: 0 }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("main", _hoisted_2, [
                    _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)("valueLists.title")), 1 /* TEXT */),
                    (_unref(store).loadError)
                        ? (_openBlock(), _createBlock(ModuleState, {
                            key: 0,
                            type: "error",
                            "data-testid": "value-lists-error",
                            title: _unref(t)('valueLists.loadError'),
                            "cta-icon": "ri-refresh-line",
                            "cta-label": _unref(t)('common.retry'),
                            onCta: _cache[0] || (_cache[0] = ($event) => (_unref(store).load()))
                        }, null, 8 /* PROPS */, ["title", "cta-label"]))
                        : (_openBlock(), _createBlock(_unref(TableShell), {
                            key: 1,
                            query: _unref(tableQuery),
                            views: _unref(tableViews),
                            labels: tableLabels.value,
                            permissions: { canSaveView: false, canShareView: false },
                            "show-search": false,
                            "show-filter": false,
                            loading: _unref(store).isLoading,
                            bordered: ""
                        }, {
                            table: _withCtx(() => [
                                _createVNode(_unref(DataTable), {
                                    columns: columns.value,
                                    data: pageRows.value,
                                    "sticky-header": "",
                                    "empty-text": _unref(t)('valueLists.empty'),
                                    "region-label": _unref(t)('valueLists.title')
                                }, null, 8 /* PROPS */, ["columns", "data", "empty-text", "region-label"])
                            ]),
                            pagination: _withCtx(() => [
                                (tabRows.value.length > 0)
                                    ? (_openBlock(), _createBlock(_unref(Pager), _mergeProps({
                                        key: 0,
                                        "current-page": _unref(tableQuery).state.page,
                                        "total-pages": totalPages.value,
                                        "total-items": tabRows.value.length,
                                        "items-per-page": _unref(tableQuery).state.pageSize
                                    }, pagerLabels.value, {
                                        "rows-per-page-options": ROW_OPTIONS,
                                        "show-go-to-page": "",
                                        onPageChange: _unref(tableQuery).setPage,
                                        onItemsPerPageChange: _unref(tableQuery).setPageSize
                                    }), null, 16 /* FULL_PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "onPageChange", "onItemsPerPageChange"]))
                                    : _createCommentVNode("v-if", true)
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["query", "views", "labels", "loading"]))
                ])
            ]));
        };
    }
});
